// ============================================================
// DashboardHero.tsx
// Dashboard "Command centre" hero — ported value-for-value from the
// approved mockup (casevo-dashboard-premium.html, option 2):
//   .hero   radius 20px · padding 34/34/64 · gradient 125deg
//           #16321f 0% → #1f4a33 45% → #2d6a4f 100%
//           (dark: #14261c → #1b3326 50% → #24402f + inset 1px white/6%)
//   ::before radial glow 460×460 at right -120 / top -160,
//            rgba(116,198,157,.22) → transparent 65%
//   ::after  dot grid radial-gradient(rgba(255,255,255,.07) 1px) 18px,
//            masked linear-gradient(90deg, transparent 40%, #000)
//   grid     1fr 1.25fr · gap 28 · stat tiles 3 cols gap 12
//   .glass   bg white/8% · border white/14% · radius 14 · pad 16/18 · blur 2px
// The three stat tiles keep their status colours (red / amber / green
// dots), never gold. Overdue → Tasks (Overdue); Ready → my open cases.
// Due soon shows what it counts — "3 tasks · 2 deadlines" — and each part is
// its own button: tasks → Tasks (Due in 7 days); deadlines → the dashboard's
// "Your next deadlines" list, narrowed to those deadlines.
// Under the summary, only when there are any: a quiet amber link "N cases
// with no limitation date →" (opens Cases on the "No limitation date" filter).
// ============================================================
import React from 'react'

type Count = number | null

interface DashboardHeroProps {
  dateLabel: string            // e.g. "Saturday 26 September"
  greeting: string             // e.g. "Good morning, Demo2."
  overdue: Count
  dueSoonTasks: Count
  dueSoonDeadlines: Count
  ready: Count
  overdueError?: boolean
  dueSoonError?: boolean
  readyError?: boolean
  onOverdue?: () => void
  onDueSoonTasks?: () => void
  onDueSoonDeadlines?: () => void
  onReady?: () => void
  // "N cases with no limitation date →" under the summary (only when N > 0).
  noLimitation?: Count
  onNoLimitation?: () => void
}

// ── Banner summary line (zero / one / several) ──────────────
const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const say = (n: number): string => (n < WORDS.length ? WORDS[n] : String(n))
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

export const buildHeroSummary = (overdue: Count, dueTasks: Count, dueDeadlines: Count, ready: Count): string => {
  const dueSoon: Count = dueTasks === null && dueDeadlines === null ? null : (dueTasks ?? 0) + (dueDeadlines ?? 0)
  if (overdue === null && dueSoon === null && ready === null) return 'Getting your day ready…'
  const parts: string[] = []
  if (overdue && overdue > 0) parts.push(`${say(overdue)} overdue ${overdue === 1 ? 'task' : 'tasks'}`)
  if (dueTasks && dueTasks > 0) parts.push(`${say(dueTasks)} ${dueTasks === 1 ? 'task' : 'tasks'} due in the next 7 days`)
  if (dueDeadlines && dueDeadlines > 0) parts.push(`${say(dueDeadlines)} case ${dueDeadlines === 1 ? 'deadline' : 'deadlines'} in the next 7 days`)
  if (ready && ready > 0) parts.push(`${say(ready)} ${ready === 1 ? 'case' : 'cases'} ready for you`)
  if (parts.length === 0) {
    // Everything known and zero → all clear. (Partially-unknown with no
    // positives → stay neutral rather than claiming "all clear".)
    return overdue === 0 && dueSoon === 0 && ready === 0
      ? "You're all clear — nothing overdue and nothing due in the next 7 days."
      : 'Nothing pressing right now.'
  }
  const joined = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  return `${cap(joined)}.`
}

// ── Due soon tile — "3 tasks · 2 deadlines", each part its own button ──
const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`
// Both parts are ALWAYS clickable — at 0 they open their (empty) destination.
const PART = 'rounded-md -mx-1 px-1 underline-offset-2 hover:underline hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
const DueSoonTile: React.FC<{ tasks: Count; deadlines: Count; error?: boolean; onTasks?: () => void; onDeadlines?: () => void }> = ({ tasks, deadlines, error, onTasks, onDeadlines }) => {
  const total = tasks === null && deadlines === null ? null : (tasks ?? 0) + (deadlines ?? 0)
  return (
    <div className="min-w-0 text-left rounded-[14px] px-3 py-3 sm:px-[18px] sm:py-4 bg-white/[0.08] border border-white/[0.14] backdrop-blur-[2px]">
      <div className="flex items-center gap-[7px] text-[10px] sm:text-[11px] tracking-[0.1em] sm:tracking-[0.14em] whitespace-nowrap uppercase font-semibold text-white/80">
        <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: '#f1c06a', boxShadow: '0 0 0 3px #f1c06a33' }} />
        Due soon
      </div>
      <div className={`text-[30px] sm:text-[40px] font-semibold leading-none mt-2.5 sm:mt-3 tabular-nums ${total === null ? 'opacity-50' : ''}`}>
        {total === null ? '—' : total}
      </div>
      <div className="text-[11.5px] sm:text-[12.5px] text-white/[0.72] mt-1.5 flex flex-wrap items-center gap-x-1.5">
        {error ? "Couldn't load" : (
          <>
            <button type="button" className={PART} onClick={onTasks}
              aria-label={tasks === null ? 'Tasks due in the next 7 days — open in Tasks' : `${plural(tasks, 'task', 'tasks')} due in the next 7 days — open in Tasks`}>
              {tasks === null ? '— tasks' : plural(tasks, 'task', 'tasks')}
            </button>
            <span aria-hidden="true">·</span>
            <button type="button" className={PART} onClick={onDeadlines}
              aria-label={deadlines === null ? 'Case deadlines in the next 7 days — show them' : `${plural(deadlines, 'case deadline', 'case deadlines')} in the next 7 days — show them`}>
              {deadlines === null ? '— deadlines' : plural(deadlines, 'deadline', 'deadlines')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Frosted stat tile ───────────────────────────────────────
interface GlassTileProps {
  dot: string
  label: string
  count: Count
  caption: string
  error?: boolean
  onClick?: () => void
}
const GlassTile: React.FC<GlassTileProps> = ({ dot, label, count, caption, error, onClick }) => {
  const base =
    'min-w-0 text-left rounded-[14px] px-3 py-3 sm:px-[18px] sm:py-4 bg-white/[0.08] border border-white/[0.14] backdrop-blur-[2px]'
  const interactive = onClick
    ? ' cursor-pointer transition-colors hover:bg-white/[0.13] hover:border-white/[0.22] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
    : ''
  const inner = (
    <>
      <div className="flex items-center gap-[7px] text-[10px] sm:text-[11px] tracking-[0.1em] sm:tracking-[0.14em] whitespace-nowrap uppercase font-semibold text-white/80">
        <span
          aria-hidden="true"
          className="inline-block w-2 h-2 rounded-full shrink-0"
          style={{ background: dot, boxShadow: `0 0 0 3px ${dot}33` }}
        />
        {label}
      </div>
      <div className={`text-[30px] sm:text-[40px] font-semibold leading-none mt-2.5 sm:mt-3 tabular-nums ${count === null ? 'opacity-50' : ''}`}>
        {count === null ? '—' : count}
      </div>
      <div className="text-[11.5px] sm:text-[12.5px] text-white/[0.72] mt-1.5">{error ? "Couldn't load" : caption}</div>
    </>
  )
  return onClick ? (
    <button type="button" onClick={onClick} className={base + interactive} aria-label={`${label}: ${count ?? 'unknown'} — ${caption}`}>
      {inner}
    </button>
  ) : (
    <div className={base}>{inner}</div>
  )
}

const DashboardHero: React.FC<DashboardHeroProps> = ({
  dateLabel, greeting, overdue, dueSoonTasks, dueSoonDeadlines, ready, overdueError, dueSoonError, readyError, onOverdue, onDueSoonTasks, onDueSoonDeadlines, onReady,
  noLimitation, onNoLimitation,
}) => (
  <section
    className="relative overflow-hidden rounded-[20px] px-5 pt-7 pb-16 sm:px-[34px] sm:pt-[34px] sm:pb-16 text-white
               bg-[linear-gradient(125deg,#16321f_0%,#1f4a33_45%,#2d6a4f_100%)]
               dark:bg-[linear-gradient(125deg,#14261c_0%,#1b3326_50%,#24402f_100%)]
               dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
  >
    {/* ::before — soft mint radial glow, top-right */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-[120px] -top-[160px] w-[460px] h-[460px] rounded-full
                 bg-[radial-gradient(circle,rgba(116,198,157,0.22),transparent_65%)]"
    />
    {/* ::after — faint dot grid, fading in from the left */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,.07) 1px, transparent 1px)',
        backgroundSize: '18px 18px',
        maskImage: 'linear-gradient(90deg, transparent 40%, #000)',
        WebkitMaskImage: 'linear-gradient(90deg, transparent 40%, #000)',
      }}
    />
    <div className="relative z-[1] grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-7 items-center">
      <div>
        <div className="text-[12px] tracking-[0.16em] uppercase font-semibold text-casevo-gold">{dateLabel}</div>
        <h1 className="text-[30px] sm:text-[38px] font-semibold tracking-[-0.02em] leading-[1.1] mt-2.5">{greeting}</h1>
        {/* min-height = the sentence's usual wrap (3 lines narrow, 2 wider), so
            the one-line "Getting your day ready…" doesn't grow the hero (and
            push the dashboard down) when the counts arrive. */}
        <p className="text-[14.5px] leading-[1.45] text-white/75 mt-2.5 min-h-[4.35em] sm:min-h-[2.9em]">{buildHeroSummary(overdue, dueSoonTasks, dueSoonDeadlines, ready)}</p>
        {!!noLimitation && noLimitation > 0 && (
          <button type="button" onClick={onNoLimitation}
            className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#f6d77e] rounded-md -mx-1 px-1 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" />
            </svg>
            {noLimitation} {noLimitation === 1 ? 'case' : 'cases'} with no limitation date
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <GlassTile dot="#f28b7d" label="Overdue" count={overdue} caption="Past due date" error={overdueError} onClick={onOverdue} />
        <DueSoonTile tasks={dueSoonTasks} deadlines={dueSoonDeadlines} error={dueSoonError} onTasks={onDueSoonTasks} onDeadlines={onDueSoonDeadlines} />
        <GlassTile dot="#74c69d" label="Ready" count={ready} caption="Assigned to you" error={readyError} onClick={onReady} />
      </div>
    </div>
  </section>
)

export default DashboardHero
